import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Badge, Button, ModalWrapper, ModalCloseButton, PillTab } from '../common';
import { shoppingService } from '../../api/services';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';
import { useMoney } from '../../hooks/useMoney';
import { translateCategoryName } from '../../constants/shoppingI18n';

/**
 * Figma: 프라이빗 쇼핑 서비스 상품선택 (600x700)
 * Browse: 3-col product grid, + only (no -), icon changes when selected
 * Selected: horizontal card list with qty stepper
 */
export default function ShoppingSelectModal({ onClose, onConfirm, initialCart }) {
  const { t, i18n } = useTranslation();
  const money = useMoney();
  const [activeCat, setActiveCat] = useState('');
  const [cart, setCart] = useState(initialCart || {});
  const [view, setView] = useState('browse');
  const [allProducts, setAllProducts] = useState([]);
  const [categoryTabs, setCategoryTabs] = useState([]);

  useEffect(() => {
    shoppingService.getRetailCategories().then((cats) => {
      setCategoryTabs(cats);
      if (cats.length > 0) setActiveCat(cats[0].id);
    }).catch((e) => console.warn(e.message));
    shoppingService.getRetailProducts().then((data) => {
      // BugHerd #77: BE 응답의 image_url 을 매핑에서 누락해 어드민 저장 이미지가 모달에 표시
      // 안 되던 원인. 함께 보존.
      setAllProducts(data.map((p) => ({
        id: p.id, category: p.category, name: p.name,
        price: p.price, desc: p.description || '',
        image_url: p.image_url || null,
      })));
    }).catch((e) => console.warn(e.message));
  }, []);

  // 카테고리 탭 라벨 + 상품 (이름/설명/카테고리 라벨) 다국어 자동 번역 (#11)
  const tabsLabelBundle = useMemo(() => categoryTabs.map((c) => c.label || ''), [categoryTabs]);
  const tTabsLabel = useDeepLTranslated(tabsLabelBundle);
  const translatedTabs = useMemo(
    () => categoryTabs.map((c, i) => ({ ...c, label: tTabsLabel[i] || c.label })),
    [categoryTabs, tTabsLabel]
  );
  // BugHerd #79 follow-up: i18n.language 기준으로 카테고리 코드 → 라벨 정적 매핑.
  // 기존엔 raw 코드(oliveyoung) 가 ko 모드에서도 그대로 노출되던 문제.
  const beLang = (i18n.language || 'ko').slice(0, 2);
  const productTextsBundle = useMemo(() => {
    const out = [];
    allProducts.forEach((p) => {
      out.push(p.name || '', p.desc || '', translateCategoryName(p.category || '', beLang));
    });
    return out;
  }, [allProducts, beLang]);
  const tProductTexts = useDeepLTranslated(productTextsBundle);
  const productOf = (p, i) => ({
    ...p,
    name: tProductTexts[i * 3] || p.name,
    desc: tProductTexts[i * 3 + 1] || p.desc,
    categoryLabel: tProductTexts[i * 3 + 2] || translateCategoryName(p.category || '', beLang),
  });

  const filtered = allProducts
    .map((p, i) => ({ ...productOf(p, i), _origIdx: i }))
    .filter((p) => p.category === activeCat);
  const cartItems = Object.entries(cart).filter(([, qty]) => qty > 0);
  const cartCount = cartItems.reduce((sum, [, qty]) => sum + qty, 0);
  const totalPrice = cartItems.reduce((sum, [id, qty]) => {
    const p = allProducts.find((pp) => pp.id === Number(id));
    return sum + (p ? p.price * qty : 0);
  }, 0);

  const addToCart = (id) => setCart((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  const isInCart = (id) => (cart[id] || 0) > 0;
  const updateQty = (id, newQty) => {
    setCart((prev) => {
      const val = Math.max(0, newQty);
      if (val === 0) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: val };
    });
  };
  const removeFromCart = (id) => setCart((prev) => { const n = { ...prev }; delete n[id]; return n; });

  const handleConfirm = () => {
    // #205 방어: cart 에 allProducts 에 없는 stale id (이전 draft 복원 등) 가 있으면
    // p.category 에서 TypeError → confirm 자체 실패하던 회귀. 같은 null guard 가
    // Selected view 렌더(line 217)엔 이미 있음. 여기도 동일하게 filter 처리.
    const products = cartItems
      .map(([id, qty]) => {
        const p = allProducts.find((pp) => pp.id === Number(id));
        if (!p) return null;
        // BugHerd #88: image_url 포함 — BookingSettings 「선택 품목」 카드에서 이미지 노출용.
        return { id: Number(id), qty, category: p.category, name: p.name, price: p.price, image_url: p.image_url };
      })
      .filter(Boolean);
    onConfirm(cart, products, totalPrice);
  };

  // #205: selected view 에서 카테고리 탭 클릭 시 setActiveCat 만 호출돼 화면 무반응.
  // 카트 항목만 보여주는 selected view 에서 탭을 누른 의도는 "다른 상품 더 담기" 이므로
  // 항상 browse view 로 전환. browse 에서는 setView('browse') no-op.
  const CategoryTabs = () => (
    <div className="px-4 py-3 shrink-0">
      <PillTab tabs={translatedTabs} activeTab={activeCat} onTabChange={(cat) => { setActiveCat(cat); setView('browse'); }} />
    </div>
  );

  /* Figma: Bottom bar - cart icon (28px) with count badge + total (700 24px) + 선택 완료 (140x48) */
  const BottomBar = () => (
    <div className="px-4 py-4 border-t border-mono-200 flex items-center justify-between shrink-0">
      <div
        className="flex items-center gap-2 cursor-pointer"
        onClick={() => setView(view === 'browse' ? 'selected' : 'browse')}
      >
        <div className="relative">
          <Icon name="cart" size={28} className="text-mono-900" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-orange-500 text-white text-2xs font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
              {cartCount}
            </span>
          )}
        </div>
        <span className="text-xl sm:text-2xl font-bold text-mono-900">{money(totalPrice)}{/^US\$/.test(String(money(totalPrice))) ? ' ~' : ''}</span>
      </div>
      <Button width="responsive-140" onClick={view === 'browse' ? () => setView('selected') : handleConfirm}>{t('common.select_done')}</Button>
    </div>
  );

  /* ===== Browse View ===== */
  if (view === 'browse') {
    return (
      <ModalWrapper maxWidth="modal-md" padding="" gap="" className="max-h-[700px]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0">
          <h2 className="text-xl font-bold text-mono-900">{t('common.shopping_modal_title')}</h2>
          <ModalCloseButton onClick={onClose} size="sm" />
        </div>

        <CategoryTabs />

        {/* Product grid - 3 columns */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filtered.map((product) => {
              const selected = isInCart(product.id);
              return (
                <div key={product.id} className="flex flex-col gap-2.5">
                  {/* Image - Figma: ~178x178, r=8, bg #F1F3F7
                      BugHerd #77: 어드민이 저장한 product.image_url 가 있으면 실제 이미지 노출.
                      기존엔 placeholder Icon 만 그려 클라가 저장 이미지 안 보인다고 보고. */}
                  <div className="aspect-square bg-mono-100 rounded-lg relative flex items-center justify-center p-2.5 overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name || ''} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <Icon name="image" size={32} className="text-mono-400 opacity-30" />
                    )}
                    {selected ? (
                      <div className="absolute bottom-2.5 right-2.5 flex items-center bg-white rounded-lg shadow-card overflow-hidden border border-mono-200">
                        <button
                          onClick={() => updateQty(product.id, cart[product.id] - 1)}
                          className="w-8 h-8 flex items-center justify-center text-mono-900 hover:bg-mono-50 transition text-sm"
                        >
                          −
                        </button>
                        <span className="w-5 text-center text-2sm font-bold text-mono-900">{cart[product.id]}</span>
                        <button
                          onClick={() => addToCart(product.id)}
                          className="w-8 h-8 flex items-center justify-center text-mono-900 hover:bg-mono-50 transition text-sm"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => addToCart(product.id)}
                        className="absolute bottom-2.5 right-2.5 w-10 h-10 rounded-lg shadow-card flex items-center justify-center bg-white text-mono-500 hover:text-orange-500 transition"
                      >
                        <Icon name="cart" size={28} />
                      </button>
                    )}
                  </div>
                  {/* Info */}
                  <div className="flex flex-col gap-0.5">
                    <Badge variant="shopping">{product.categoryLabel}</Badge>
                    <p className="text-sm font-bold text-mono-900">{product.name}</p>
                    <p className="text-sm font-normal text-mono-900">{money(product.price)}</p>
                    <p className="text-2sm font-normal text-mono-600">{product.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <BottomBar />
      </ModalWrapper>
    );
  }

  /* ===== Selected Products View ===== */
  return (
    <ModalWrapper maxWidth="modal-md" padding="" gap="" className="max-h-[700px]">
      {/* Header - back + title + close */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0">
        <button onClick={() => setView('browse')} className="text-mono-900 hover:text-orange-500">
          <Icon name="chevron-left" size={24} />
        </button>
        <h2 className="text-xl font-bold text-mono-900">{t('common.selected_products_title')}</h2>
        <ModalCloseButton onClick={onClose} size="sm" />
      </div>

      <CategoryTabs />

      {/* Selected products list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-3">
        {cartItems.length === 0 ? (
          <div className="text-center py-10 text-mono-400 text-sm">{t('common.no_selected_products')}</div>
        ) : (
          cartItems.map(([id, qty]) => {
            const idx = allProducts.findIndex((pp) => pp.id === Number(id));
            const p = idx >= 0 ? productOf(allProducts[idx], idx) : null;
            if (!p) return null;
            return (
              <div key={id} className="flex gap-2.5">
                {/* Image - Figma: 140x140, r=8, bg #F1F3F7
                    어드민이 저장한 product.image_url 가 있으면 실제 이미지 노출. browse view 와 동일 규칙. */}
                <div className="w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] bg-mono-100 rounded-lg relative flex items-center justify-center shrink-0 overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name || ''} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <Icon name="image" size={32} className="text-mono-400 opacity-30" />
                  )}
                  <div className="absolute bottom-2.5 right-2.5 w-8 h-8 bg-white rounded-lg shadow-card flex items-center justify-center">
                    <Icon name="cart" size={18} className="text-mono-500" />
                  </div>
                </div>
                {/* Details */}
                <div className="flex-1 min-w-0 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="shopping">{p.categoryLabel}</Badge>
                    <button
                      onClick={() => removeFromCart(id)}
                      className="text-2sm font-normal text-mono-900 hover:text-orange-500"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                  <p className="text-sm font-bold text-mono-900">{p.name}</p>
                  {/* Price + qty stepper - Figma: pill r=30, border arrow-muted */}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-sm font-normal text-mono-900">{money(p.price)}</span>
                    <div className="flex items-center border border-mono-300 rounded-full overflow-hidden">
                      <button
                        onClick={() => updateQty(Number(id), qty - 1)}
                        className="w-8 h-[37px] flex items-center justify-center text-sm text-mono-900 hover:bg-mono-50 transition"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-normal text-mono-900">{qty}</span>
                      <button
                        onClick={() => updateQty(Number(id), qty + 1)}
                        className="w-8 h-[37px] flex items-center justify-center text-sm text-mono-900 hover:bg-mono-50 transition"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <BottomBar />
    </ModalWrapper>
  );
}
